import React, { useState, useEffect } from 'react';
import CMSDashboardPage from './CMSDashboardPage'; // Will create this next

const LoginPage = ({ initialTab = 'translations' }) => {
  const [passphrase, setPassphrase] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' }); // type: 'success' or 'error'
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing valid key on mount
  useEffect(() => {
    const storedKey = localStorage.getItem('cmsKey');
    if (storedKey) {
      // Basic check: assume key is valid if present. 
      // A more robust check would re-validate against a lightweight auth endpoint.
      setIsAuthenticated(true);
      // No initial message needed if already authenticated, will go straight to dashboard
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    if (!passphrase) {
      setMessage({ type: 'error', text: 'Please enter the CMS passphrase.' });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/cms/versions', {
        method: 'GET',
        headers: {
          'X-CMS-Key': passphrase,
        },
      });

      if (response.ok) {
        localStorage.setItem('cmsKey', passphrase);
        setIsAuthenticated(true);
        // No message needed here, will transition to dashboard
      } else if (response.status === 401) {
        localStorage.removeItem('cmsKey');
        setMessage({ type: 'error', text: 'Access Denied: Invalid passphrase.' });
      } else {
        localStorage.removeItem('cmsKey');
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        setMessage({ type: 'error', text: `Error: ${response.status} - ${errorData.error || 'Could not validate passphrase.'}` });
      }
    } catch (error) {
      localStorage.removeItem('cmsKey');
      setMessage({ type: 'error', text: `Network or server error: ${error.message}` });
    }
    setIsLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('cmsKey');
    setIsAuthenticated(false);
    setPassphrase('');
    setMessage({ type: '', text: '' }); // Clear any messages on logout
  };

  // Improved inline styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start', // Align to top for when dashboard content appears
      minHeight: '100vh',
      backgroundColor: '#f0f2f5',
      paddingTop: '50px',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    loginBox: {
      padding: '40px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      width: '100%',
      maxWidth: '400px',
      textAlign: 'center',
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '30px',
    },
    input: {
      width: 'calc(100% - 22px)',
      padding: '12px 10px',
      fontSize: '16px',
      border: '1px solid #d9d9d9',
      borderRadius: '4px',
      marginBottom: '20px',
      boxSizing: 'border-box',
    },
    button: {
      width: '100%',
      padding: '12px 20px',
      fontSize: '16px',
      fontWeight: '500',
      color: 'white',
      backgroundColor: '#1890ff', // A common Ant Design blue
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.3s',
    },
    buttonHover: { // Pseudo-state, would need JS to apply or use CSS classes
      backgroundColor: '#40a9ff',
    },
    message: {
      marginTop: '20px',
      padding: '10px',
      borderRadius: '4px',
      fontSize: '14px',
    },
    successMessage: {
      backgroundColor: '#e6f7ff',
      color: '#1890ff',
      border: '1px solid #91d5ff',
    },
    errorMessage: {
      backgroundColor: '#fff1f0',
      color: '#f5222d',
      border: '1px solid #ffa39e',
    }
  };

  if (isAuthenticated) {
    // Pass handleLogout to the dashboard so it can have a logout button
    return <CMSDashboardPage onLogout={handleLogout} initialTab={initialTab} />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        <h2 style={styles.title}>CMS Access</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Enter Passphrase"
            style={styles.input}
            disabled={isLoading}
          />
          <button 
            type="submit" 
            disabled={isLoading} 
            style={styles.button}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.buttonHover.backgroundColor}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.button.backgroundColor}
          >
            {isLoading ? 'Verifying...' : 'Login'}
          </button>
        </form>
        {message.text && (
          <div style={{
            ...styles.message,
            ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
          }}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage; 